import * as React from 'react';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { parseContent } from '@ally/ally-md-parser';
import { Box, Button, Media, Space, Spinner, TextBody, } from '@ally/metronome-ui';
import { toCamelCase } from '@ally/utilitarian';
import { CallUsModal } from '../../../../Modals';
import { useTransmit } from '../../../../TransmitContext';
import { LoginHeading } from '../../Layout/Layout';
import { GettingInSvg } from '../../Svgs';
import { MultiConsent, useMultiConsent, } from './MultiConsent';
const callUsModalTrigger = 'call-us-modal-trigger';
const StyledBox = styled(Box) `
  display: flex;
  justify-content: space-between;
`;
const StyledTextBody = styled(TextBody) `
  ${({ theme: { colors } }) => `
    a {
      color: ${colors.bluesuedeshoes};
      text-decoration: none;
      font-weight: bold;
    }
  `}
`;
const StyledContentWrapped = styled(Box) `
  ${(props) => `
    ${!props.isInPortal
    ? `${props.theme.media.up('lg')} {
          width: ${(7 / 12) * 100}%; // based on a 12 column grid
        }`
    : ''}
  `}
`;
const StyledSpinnerWrapper = styled(Box) `
  width: 100%;
  display: flex;
  justify-content: center;
`;
// svg right hand side
const StyledSvgWrapper = styled(Box) `
  ${({ theme: { media } }) => `
    ${media.up('lg')} {
      width: ${(4 / 12) * 100}%; // based on a 12 column grid
      order: 1;
    }
  `}
`;
const ConsentContent = ({ jsonBodies, jsonButton, shouldFocusOnMount, consentType, showModal, }) => {
    const { asyncStatus, showNextConsent } = useMultiConsent();
    const handleOnClick = () => {
        showNextConsent();
    };
    const handleCallUsOnClick = (e) => {
        e.preventDefault();
        showModal();
    };
    React.useEffect(() => {
        document.querySelectorAll(`.${callUsModalTrigger}`).forEach(el => {
            el.addEventListener('click', handleCallUsOnClick);
        });
        return () => {
            // Removing the event listener as React is unaware of it
            document.querySelectorAll(`.${callUsModalTrigger}`).forEach(el => {
                el.removeEventListener('click', handleCallUsOnClick);
            });
        };
    });
    const ConsentBodies = jsonBodies.map((body, index) => {
        const parsedContent = parseContent(body);
        /**
         * Replaces all instances of "call us" with an anchor used as the trigger component for the Call Us modal
         *
         * Note: it intentionally does not match on "call us at" as that is followed by a phone number instead
         *  It also must be followed by a whitespace or non-letter char
         */
        const callUsInjectedContent = parsedContent.replace(/\scall us(?!\sat|[A-z])/g, `<a href="javascript:void(0);" class="${callUsModalTrigger}" allytmfn="callUs"> call us</a>`);
        return (React.createElement(Space, { mb: "sm", key: uuidv4() },
            React.createElement(StyledTextBody, Object.assign({ tag: "p", size: "sm", "data-testid": `secure-consent-body_${index}`, 
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                dangerouslySetInnerHTML: { __html: callUsInjectedContent } }, (shouldFocusOnMount && index === 0 ? { focusOnMount: true } : {})))));
    });
    return (React.createElement(Box, null,
        ConsentBodies,
        jsonButton && (React.createElement(Button, { text: jsonButton, onClick: handleOnClick, loading: asyncStatus === 'Loading', "data-testid": "secure-consent-button", allytmln: `${consentType}-${toCamelCase(jsonButton)}` }))));
};
export const SecureConsentForm = ({ onComplete, status, error, consents = [], hostServicesOverride = undefined, shouldShowAllLOBs = false, userData = {}, }) => {
    const { isInPortal } = useTransmit();
    const [loadedConsents, setLoadedConsents] = React.useState([]);
    const [shouldShowCallUsModal, setShouldShowCallUsModal,] = React.useState(false);
    const showModal = () => {
        setShouldShowCallUsModal(true);
    };
    const onOpenChange = (open) => {
        if (open === false) {
            setShouldShowCallUsModal(false);
        }
    };
    // load all esigns before rendering
    React.useEffect(() => {
        const promises = consents.map(({ contentUrl, consentType }) => {
            return fetch(contentUrl)
                .then(res => res.json())
                .then(data => (Object.assign(Object.assign({}, data), { consentType })))
                .catch(err => {
                /* istanbul ignore next */
                throw err;
            });
        });
        Promise.all(promises)
            .then(results => {
            const shouldFocusOnMount = results.length === 1;
            const mappedConsents = results.map((result, idx) => {
                const { body, cta, title, consentType } = result;
                return {
                    title,
                    component: (React.createElement(ConsentContent, { consentType: consentType, key: uuidv4(), jsonBodies: body, jsonButton: cta, shouldFocusOnMount: shouldFocusOnMount, showModal: showModal })),
                };
            });
            setLoadedConsents(mappedConsents);
        })
            .catch(reason => {
            // If the consents fail to load we send the user to the
            // temporarily unavailable screen
            setLoadedConsents([
                {
                    title: 'Error',
                    component: React.createElement(Redirect, { to: "/temp-unavailable" }),
                },
            ]);
        });
    }, []);
    const { hostServices } = useTransmit();
    const analytics = (hostServices === null || hostServices === void 0 ? void 0 : hostServices.analytics) || (hostServicesOverride === null || hostServicesOverride === void 0 ? void 0 : hostServicesOverride.analytics);
    React.useEffect(() => {
        var _a;
        analytics === null || analytics === void 0 ? void 0 : analytics.trackPageView({
            eventType: 'pageview',
            pageName: 'AOS:Login:Electronic Consent and Terms of Use',
        });
        const { cupid, guid, profileId, tpn } = userData;
        (_a = window.allytm) === null || _a === void 0 ? void 0 : _a.event('customEvent', {
            name: 'userConsents',
            type: 'userAttribute',
            attr: {
                user: {
                    attributes: {
                        cupid,
                        guid,
                        profileID: profileId,
                        tpn,
                    },
                },
            },
        });
    }, []);
    let title = 'Electronic Consent and Terms of Use';
    let leadCopy = 'Almost there. Before we finish, we just need you to agree to our Electronic Consent and Terms of Use.';
    if (userData.show_update_message) {
        title = 'Updated Agreement and Terms of Use';
        leadCopy =
            'We need you to reconfirm you’re okay with receiving some info online and signing documents electronically. Agreeing won’t impact how you’re receiving statements.';
    }
    if (consents && consents.length === 0) {
        return React.createElement(Redirect, { to: "/temp-unavailable" });
    }
    return (React.createElement(StyledBox, null,
        React.createElement(StyledContentWrapped, { isInPortal: isInPortal },
            React.createElement(LoginHeading, { title: title }),
            shouldShowCallUsModal && (React.createElement(CallUsModal, { disableTrigger: true, initialOpen: true, onOpenChange: onOpenChange })),
            loadedConsents.length >= 1 ? (React.createElement(React.Fragment, null,
                React.createElement(Box, { mb: "md" },
                    React.createElement(TextBody, { size: "sm", tag: "p", "data-testid": "secure-consent-lead-copy" }, leadCopy)),
                React.createElement(MultiConsent, { consents: loadedConsents, onComplete: onComplete, status: status }))) : (React.createElement(StyledSpinnerWrapper, null,
                React.createElement(Spinner, null)))),
        !isInPortal && (React.createElement(StyledSvgWrapper, { "data-testid": "getting-in-image" },
            React.createElement(Media.LgUp, null,
                React.createElement(GettingInSvg, null)))),
        React.createElement(CallUsModal, { shouldShowAllLOBs: shouldShowAllLOBs, disableTrigger: true })));
};
//# sourceMappingURL=SecureConsentForm.js.map